package com.eladfinish.bible.contest.screens.chapterComplete.features.progressiveExposure

import com.eladfinish.bible.contest.constants.StringConstants.COLON_SPLITTER
import com.eladfinish.bible.contest.screens.browse.tableOfContents.model.CompleteType

/**
 * Pure text renderer for progressive exposure.
 *
 * Important: This is intentionally Android-free, so it can run in shared KMP code.
 */
class ExposureTextRenderer(
    private val eachVerseInNewLine: Boolean,
    private val makaf: Char,
) {

    fun renderVerseStep(
        state: ProgressiveExposureState,
    ): ProgressiveExposureState {
        return renderStep(
            state = state,
            completeType = CompleteType.NEXT_EXPOSURE_VERSE,
        )
    }

    fun renderHalfStep(
        state: ProgressiveExposureState,
    ): ProgressiveExposureState {
        return renderStep(
            state = state,
            completeType = CompleteType.NEXT_EXPOSURE_HALF,
        )
    }

    fun renderWordStep(
        state: ProgressiveExposureState,
    ): ProgressiveExposureState {
        return renderStep(
            state = state,
            completeType = CompleteType.NEXT_EXPOSURE_WORD,
        )
    }

    private fun renderStep(
        state: ProgressiveExposureState,
        completeType: CompleteType,
    ): ProgressiveExposureState {
        if (state.isFinished()) return state

        var currentSegment = state.currentSegment
        var currentWord = state.currentWord

        val out = StringBuilder(
            /* content = */ state.renderedTextSoFar,
        )

        if (currentSegment >= state.elementsNumber) {
            return state
        }

        val location = ProgressiveExposureSelectors.locate(
            verseStartSegments = state.verseStartSegments,
            currentSegment = currentSegment,
        )

        val verseParts = state.partitionedChapter[location.verseIndex]
        val otPasuk = verseParts[0][0]

        // After otPasuk, there are 2 or 3 (or more) parts.
        val parts: List<List<String>> = verseParts.drop(1)
        val partIndex = location.partIndexInVerse.coerceIn(0, parts.lastIndex)
        val partWords = parts[partIndex]
        val isLastPartInVerse = partIndex == parts.lastIndex

        fun ensureStepSeparator() {
            if (out.isEmpty()) return
            val last = out.last()
            val lastIsWhitespace = last == ' ' || last == '\n' || last == '\t'
            val lastIsMakaf = last == makaf
            if (!lastIsWhitespace && !lastIsMakaf) {
                out.append(" ")
            }
        }

        fun appendColon() {
            // No leading space here – must stick to the last word.
            out.append(COLON_SPLITTER)
            if (eachVerseInNewLine) {
                out.append("\n")
            } else {
                out.append(" ")
            }
        }

        fun appendPartFrom(
            fromWord: Int,
            endsWithColon: Boolean,
        ) {
            val start = fromWord.coerceIn(0, partWords.size)
            val sub = partWords.subList(start, partWords.size)

            ensureStepSeparator()

            // Prefix otPasuk only at the beginning of the first part.
            if (partIndex == 0 && fromWord == 0) {
                out.append(otPasuk)
                out.append(" ")
            }

            out.append(
                MakafJoiner.joinWordsKeepingMakaf(
                    words = sub,
                    makaf = makaf,
                ),
            )

            if (endsWithColon) {
                appendColon()
            } else {
                out.append(" ")
            }
        }

        fun advanceToNextSegment() {
            currentWord = 0
            currentSegment++
        }

        fun advanceToNextVerse() {
            val nextVerseStart = state.verseStartSegments.getOrNull(location.verseIndex + 1)
                ?: state.elementsNumber
            currentWord = 0
            currentSegment = nextVerseStart
        }

        when (completeType) {
            CompleteType.NEXT_EXPOSURE_HALF -> {
                // Reveal ONE part per tap.
                // For 3-part verses: this behaves as "third exposure".
                appendPartFrom(
                    fromWord = currentWord,
                    endsWithColon = isLastPartInVerse,
                )

                if (isLastPartInVerse) {
                    advanceToNextVerse()
                } else {
                    advanceToNextSegment()
                }
            }

            CompleteType.NEXT_EXPOSURE_VERSE -> {
                if (isLastPartInVerse) {
                    // ✅ We're already on the last part, so VERSE means "finish this part + colon".
                    appendPartFrom(
                        fromWord = currentWord,
                        endsWithColon = true,
                    )
                    advanceToNextVerse()
                } else {
                    // Finish current part remainder (no colon yet)
                    appendPartFrom(
                        fromWord = currentWord,
                        endsWithColon = false,
                    )

                    // Append remaining parts fully
                    for (i in (partIndex + 1)..parts.lastIndex) {
                        val words = parts[i]
                        val isLast = i == parts.lastIndex

                        ensureStepSeparator()
                        out.append(
                            MakafJoiner.joinWordsKeepingMakaf(
                                words = words,
                                makaf = makaf,
                            ),
                        )

                        if (isLast) {
                            appendColon()
                        } else {
                            out.append(" ")
                        }
                    }

                    advanceToNextVerse()
                }
            }

            CompleteType.NEXT_EXPOSURE_WORD -> {
                if (partWords.isEmpty()) {
                    if (isLastPartInVerse) advanceToNextVerse() else advanceToNextSegment()
                } else if (currentWord < partWords.size) {
                    // Prefix otPasuk only at the beginning of the first part.
                    if (partIndex == 0 && currentWord == 0) {
                        ensureStepSeparator()
                        out.append(otPasuk)
                        out.append(" ")
                    }

                    val word = partWords[currentWord]

                    // Add a single separator before the newly appended word (if needed).
                    ensureStepSeparator()
                    out.append(word)

                    val endsWithMakaf = word.endsWith(makaf.toString())
                    val isLastWordInPart = currentWord == partWords.lastIndex

                    if (!endsWithMakaf && !isLastWordInPart) {
                        out.append(" ")
                    }

                    if (isLastWordInPart) {
                        if (isLastPartInVerse) {
                            appendColon()
                            advanceToNextVerse()
                        } else {
                            out.append(" ")
                            advanceToNextSegment()
                        }
                    } else {
                        currentWord++
                    }
                }
            }

            else -> {
                // This renderer handles only the NEXT_EXPOSURE_* trio.
                return state
            }
        }

        val normalized = ProgressiveExposureNormalizer.normalize(
            text = out.toString(),
        )

        return state.copy(
            currentSegment = currentSegment,
            currentWord = currentWord,
            renderedTextSoFar = normalized,
        )
    }
}
